import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { components } from "@/shared/lib/api.types"
import { apiClient } from "@/shared/lib/apiClient"
import { unwrap } from "@/shared/utils/unwrap"
import type { SessionFormValues } from "../sleep.schemas"

export type SleepSession = components["schemas"]["SleepSessionDto"]

// 409 payload — the overlapping interval.
export type SleepConflict = components["schemas"]["SleepConflictResponse"]

function invalidateSleepQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["dashboard"] })
  queryClient.invalidateQueries({ queryKey: ["sessions"] })
  queryClient.invalidateQueries({ queryKey: ["statistics"] })
}

export function useCreateSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ childId, values }: { childId: string; values: SessionFormValues }) =>
      unwrap(
        apiClient.POST("/api/children/{childId}/sleep-sessions", {
          params: { path: { childId } },
          body: values,
        }),
      ),
    onSuccess: () => invalidateSleepQueries(queryClient),
  })
}

export function useUpdateSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: SessionFormValues }) =>
      unwrap(apiClient.PUT("/api/sleep-sessions/{id}", { params: { path: { id } }, body: values })),
    onSuccess: () => invalidateSleepQueries(queryClient),
  })
}

export function useDeleteSessionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(apiClient.DELETE("/api/sleep-sessions/{id}", { params: { path: { id } } })),
    onSuccess: () => invalidateSleepQueries(queryClient),
  })
}
